import { useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, UserCog, Mail, KeyRound, Save, Eye, EyeOff, Camera, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Field, Input } from '@/components/ui/Field';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/ToastContext';
import { useUserRole } from '@/hooks/useUserRole';
import { supabase } from '@/lib/supabaseClient';
import { uploadAvatar } from '@/lib/avatarApi';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin (관리자)',
  admin_low: 'Admin · Limited (제한 관리자)',
  bd_staff: 'BD Staff (BD 직원)',
  kr_staff: 'KR Staff (KR 직원)',
};

function PasswordField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <Field label={label}>
      <div className="relative">
        <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
        <Input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="pl-9 pr-9"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-disabled hover:text-ink-faint cursor-pointer"
          aria-label={show ? 'Hide password' : 'Show password'}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </Field>
  );
}

export default function Profile() {
  const { user, role } = useUserRole();
  const { push } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState((user?.user_metadata?.full_name as string) ?? (user?.user_metadata?.display_name as string) ?? '');
  const [avatarUrl, setAvatarUrl] = useState((user?.user_metadata?.avatar_url as string) ?? '');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  const handleAvatarPick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user) return;
    setAvatarUploading(true);
    try {
      const url = await uploadAvatar(user.id, file);
      setAvatarUrl(url);
      push('success', 'Profile picture updated (프로필 사진이 변경되었습니다)');
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setAvatarUploading(false);
    }
  };

  const saveAccount = async (e: FormEvent) => {
    e.preventDefault();
    setSavingAccount(true);
    const { error } = await supabase.auth.updateUser({ data: { full_name: displayName, display_name: displayName } });
    setSavingAccount(false);
    push(error ? 'error' : 'success', error ? error.message : 'Account information updated (계정 정보가 저장되었습니다)');
  };

  const savePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      push('error', 'New password must be at least 8 characters (8자 이상 입력하세요)');
      return;
    }
    if (newPassword !== confirmPassword) {
      push('error', 'New password and confirmation do not match (비밀번호가 일치하지 않습니다)');
      return;
    }
    setSavingPassword(true);
    try {
      if (user?.email) {
        const { error: verifyError } = await supabase.auth.signInWithPassword({ email: user.email, password: currentPassword });
        if (verifyError) {
          push('error', 'Current password is incorrect (현재 비밀번호가 올바르지 않습니다)');
          return;
        }
      }
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) {
        push('error', error.message);
        return;
      }
      push('success', 'Password updated (비밀번호가 변경되었습니다)');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-subtle p-6 flex justify-center">
      <div className="w-full max-w-lg space-y-4">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-muted hover:text-ink">
          <ArrowLeft className="w-3.5 h-3.5" /> Back (뒤로)
        </Link>

        <Card>
          <CardBody className="flex items-center gap-4">
            <div className="relative flex-shrink-0">
              <div className="w-16 h-16 rounded-full bg-primary-800 text-white flex items-center justify-center text-[24px] font-bold overflow-hidden">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  (displayName || user?.email || '?').slice(0, 1).toUpperCase()
                )}
              </div>
              <button
                type="button"
                onClick={handleAvatarPick}
                disabled={avatarUploading}
                className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-primary-800 text-white flex items-center justify-center border-2 border-white cursor-pointer hover:bg-primary-700 disabled:opacity-60"
                aria-label="Change profile picture"
              >
                {avatarUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
              </button>
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleAvatarChange} />
            </div>
            <div className="min-w-0">
              <p className="text-[16px] font-bold text-ink truncate">{displayName || user?.email}</p>
              <p className="text-[12.5px] text-ink-faint truncate">{user?.email}</p>
              {role && (
                <div className="mt-1.5">
                  <Badge tone="primary">{ROLE_LABEL[role] ?? role}</Badge>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Account Information (계정 정보)" icon={<UserCog className="w-4 h-4" />} />
          <CardBody className="space-y-4">
            <form onSubmit={saveAccount} className="space-y-4">
              <Field label="Display Name (표시 이름)">
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </Field>
              <Field label="Email Address (이메일)">
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
                  <Input value={user?.email ?? ''} disabled className="opacity-70 pl-9" />
                </div>
              </Field>
              <p className="text-[11px] text-ink-disabled -mt-2">
                Email cannot be changed here. Contact admin to update. (이메일은 여기서 변경할 수 없습니다)
              </p>
              <Button type="submit" loading={savingAccount}>
                <Save className="w-4 h-4" /> Save Changes (저장)
              </Button>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Change Password (비밀번호 변경)" subtitle="Use a strong password of at least 8 characters. (8자 이상의 강력한 비밀번호)" />
          <CardBody className="space-y-4">
            <form onSubmit={savePassword} className="space-y-4">
              <PasswordField label="Current Password (현재 비밀번호)" value={currentPassword} onChange={setCurrentPassword} placeholder="Enter current password" />
              <PasswordField label="New Password (새 비밀번호)" value={newPassword} onChange={setNewPassword} placeholder="Enter new password" />
              <PasswordField
                label="Confirm New Password (새 비밀번호 확인)"
                value={confirmPassword}
                onChange={setConfirmPassword}
                placeholder="Re-enter new password"
              />
              <Button type="submit" loading={savingPassword}>
                <Save className="w-4 h-4" /> Update Password (변경)
              </Button>
            </form>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
